import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { totalPlayTimetoString } from 'Utils';
import { range } from 'lodash';
import './BarChart.css';

interface BarChartProps {
  data: { tier: number; time: number }[]; // time in seconds, value is the number on the far right
  // Total tiers in the parent level. Used to render greyed-out bars for
  // tiers above the highest-played one. Passed as a prop so this chart
  // doesn't depend on the lessonReducer Redux slice — that slice doesn't
  // exist in the admin app's store, which previously threw
  // "Cannot read properties of undefined (reading 'currentLevelTiers')".
  currentLevelTiers: number;
  // The tier the user is currently on, used to color that bar differently
  // from the previously-played ones. Optional; when missing, no bar gets
  // the "current" highlight.
  currentTier?: number;
}

const BarChart: React.FC<BarChartProps> = ({ data, currentLevelTiers, currentTier }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const containerWidth = containerRef.current?.clientWidth ?? 260;
    const scaledWidth = Math.max(160, Math.min(containerWidth - 20, 300));

    const margin = { top: 16, right: 50, bottom: 16, left: 50 };
    const width = scaledWidth - margin.left - margin.right;
    const height = Math.max(60, Math.min(scaledWidth * 0.4, 100)) - margin.top - margin.bottom;
    const maxPlayedTier = data.reduce((currentGreatest, current) => {
      if (current.tier > currentGreatest && current.time > 0) {
        return current.tier;
      } else {
        return currentGreatest;
      }
    }, 0);
    const _tiersAboveCurrentTier = range(maxPlayedTier + 1, currentLevelTiers + 1);

    // Remove previous SVG content on re-render
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3
      .select(svgRef.current)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    // .attr('class', 'TierBox')

    // Define scales
    const x = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d: { tier: number; time: number }) => d.time || 0) || 0])
      .range([0, width]);

    const y = d3
      .scaleBand()
      .domain(data.map((d: { tier: number; time: number }) => `Tier ${d.tier}`))
      .range([0, height])
      .padding(0.2); // More padding to prevent overlap

    const currentTierColor = '#6f7ce4';
    const prevTierColor = '#787b84';
    const playedTierLabel = '#DAD1D4';
    const notPlayedTierLabel = '#767C8C';

    // Append bars
    svg
      .selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', (d: { tier: number; time: number }) => y(`Tier ${d.tier}`) || 0)
      .attr('width', (d: { tier: number; time: number }) => x(d.time))
      .attr('height', y.bandwidth())
      .attr('fill', (d: { tier: number; time: number }) =>
        d.tier === currentTier ? currentTierColor : prevTierColor,
      );

    // Add tier labels (left side)
    svg
      .selectAll('.tier-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'tier-label')
      .attr('x', -10) // Slightly left of the bars
      .attr(
        'y',
        (d: { tier: number; time: number }) => (y(`Tier ${d.tier}`) || 0) + y.bandwidth() / 2,
      )
      .attr('dy', '.35em')
      // .attr('text-anchor', 'end')
      .attr('fill', (d: { tier: number; time: number }) =>
        d.tier <= maxPlayedTier ? playedTierLabel : notPlayedTierLabel,
      )
      .attr('font-size', scaledWidth < 220 ? '10px' : '12px')
      .text((d: { tier: number; time: number }) => `Tier ${d.tier}`)
      .style(`text-anchor`, `start`)
      .attr('transform', `translate(-28,0)`);

    // Add value labels (right side, like "784" in your screenshot)
    svg
      .selectAll('.value-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'value-label')
      .attr('x', width + 5) // Place it to the right of the bar
      .attr(
        'y',
        (d: { tier: number; time: number }) => (y(`Tier ${d.tier}`) || 0) + y.bandwidth() / 2,
      )
      .attr('dy', '.35em')
      .style(`text-anchor`, `start`)
      .attr('fill', '#fff')
      .attr('font-size', scaledWidth < 220 ? '10px' : '12px')
      .text((d: { tier: number; time: number }) => (d.time ? totalPlayTimetoString(d.time) : ''));
  }, [data]);

  return (
    <div
      ref={containerRef}
      style={{ padding: '6px', borderRadius: '10px', width: '100%', overflow: 'hidden' }}
    >
      <svg ref={svgRef} style={{ maxWidth: '100%', height: 'auto' }}></svg>
    </div>
  );
};

export default BarChart;
